import { useState, useRef, useEffect, useCallback } from "react";
import { Navbar } from "@/components/Navbar";
import { DashSidebar } from "@/components/DashSidebar";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { hasPlan } from "@/lib/planAccess";
import { Microphone, User, PaperPlaneRight, Play, Sparkle, Plus, ArrowLeft, ClockCounterClockwise } from "@phosphor-icons/react";

const ROLE_GROUPS = {
  "Engineering & Construction": [
    "electrical_engineer",
    "electrical_technician",
    "elv_engineer",
    "mep_engineer",
    "project_engineer",
  ],
  "Technology": ["software_engineer", "data_scientist"],
  "Business & Product": ["product_manager"],
  "Other": ["general"],
};

export default function InterviewSim() {
  const { user } = useAuth();
  const paid = hasPlan(user, "pro");
  const [usage, setUsage] = useState(null);
  const [session, setSession] = useState(null);
  const [demoLocked, setDemoLocked] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [roles, setRoles] = useState([]);
  const [roleId, setRoleId] = useState("");
  const [customRole, setCustomRole] = useState("");
  const [busy, setBusy] = useState(false);
  const [setupMode, setSetupMode] = useState(false);
  const [previousSession, setPreviousSession] = useState(null);
  const [initializing, setInitializing] = useState(true);
  const endRef = useRef(null);

  useEffect(() => {
    let mounted = true;

    Promise.allSettled([
      api.get("/interview/roles"),
      api.get("/interview/latest"),
      api.get("/billing/usage"),
    ]).then(([rolesResult, latestResult, usageResult]) => {
      if (!mounted) return;

      if (rolesResult.status === "fulfilled") {
        setRoles(rolesResult.value.data.roles || []);
      } else {
        console.error("roles load failed", rolesResult.reason);
      }

      if (latestResult.status === "fulfilled") {
        const { data } = latestResult.value;
        if (data.session) {
          setSession(data.session);
          setMessages(data.session.messages || []);
        }
      }
      if (usageResult?.status === "fulfilled") setUsage(usageResult.value.data);

      // Wait for the initial session check before rendering the setup screen.
      // This prevents the role-selection screen from flashing before a saved
      // interview session is restored.
      setInitializing(false);
    });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!paid) {
      const answerCount = messages.filter((m) => m.role === "user").length;
      setDemoLocked(answerCount >= 3);
    } else {
      setDemoLocked(false);
    }
  }, [messages, paid]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const resolvedRole = useCallback(() => {
    if (customRole.trim()) return customRole.trim();
    const r = roles.find((x) => x.id === roleId);
    return r?.label || "";
  }, [roleId, customRole, roles]);

  const demoUsed = Number(usage?.interview_demo?.used_sessions || 0) >= 1;
  const paidSessionsUsed = Number(usage?.used?.interview_sessions || 0);
  const paidSessionLimit = Number(usage?.limits?.interview_sessions || 0);
  const paidSessionLimitReached = paid && paidSessionsUsed >= paidSessionLimit;

  const start = async () => {
    if (!paid && demoUsed) {
      toast.error("Your free interview demo has already been used. Upgrade to Pro or Premium to start another interview.");
      setDemoLocked(true);
      return;
    }
    if (paid && paidSessionLimitReached) {
      toast.error("You have reached your interview-session limit for this month. Upgrade when your limit resets or use Premium for more sessions.");
      return;
    }

    const role = resolvedRole();
    if (!role) {
      toast.error("Choose a role or enter your own role first.");
      return;
    }

    setBusy(true);
    try {
      const { data } = await api.post("/interview/start", { role });
      setSession(data);
      setMessages([{ role: "assistant", content: data.question, key: `q-${Date.now()}` }]);
      setSetupMode(false);
      setPreviousSession(null);
      toast.success(`Interview for ${data.role} started`);
      api.get("/billing/usage").then(({data: u}) => setUsage(u)).catch(() => {});
    } catch (err) {
      const detail = err.response?.data?.detail || "Failed to start interview";
      if (err.response?.status === 403) {
        toast.error(detail);
        if (!paid) setDemoLocked(true);
      } else {
        toast.error(detail);
      }
    } finally {
      setBusy(false);
    }
  };

  const startNew = () => {
    if (session) {
      setPreviousSession({ session, messages });
    }
    setSession(null);
    setMessages([]);
    setInput("");
    setCustomRole("");
    setRoleId("");
    setSetupMode(true);
  };

  const resumePrevious = () => {
    if (!previousSession) return;
    setSession(previousSession.session);
    setMessages(previousSession.messages || []);
    setPreviousSession(null);
    setSetupMode(false);
  };

  const send = async () => {
    if (!input.trim() || busy || !session) return;
    if (!paid && messages.filter((m) => m.role === "user").length >= 3) {
      setDemoLocked(true);
      toast.error("Your 3-answer free interview demo is complete. Upgrade to continue.");
      return;
    }
    const msg = input.trim();
    const userKey = `u-${Date.now()}`;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: msg, key: userKey }]);
    setBusy(true);
    try {
      const { data } = await api.post("/interview/reply", {
        session_id: session.session_id,
        message: msg,
      });
      setMessages((prev) => [...prev, {
        role: "assistant",
        content: data.question,
        score: data.score,
        feedback: data.feedback,
        key: `a-${Date.now()}`,
      }]);
    } catch (err) {
      const detail = err.response?.data?.detail || "Reply failed";
      toast.error(detail);
      setMessages((prev) => prev.filter((m) => m.key !== userKey));
      if (err.response?.status === 403 && !paid) setDemoLocked(true);
    } finally {
      setBusy(false);
      api.get("/billing/usage").then(({data: u}) => setUsage(u)).catch(() => {});
    }
  };

  const avgScore = (() => {
    const scored = messages.filter((m) => typeof m.score === "number" && m.score > 0);
    if (!scored.length) return null;
    return Math.round(scored.reduce((a, m) => a + m.score, 0) / scored.length);
  })();

  const renderRoleOptions = () => {
    const used = new Set();
    return Object.entries(ROLE_GROUPS).map(([group, ids]) => {
      const groupRoles = ids
        .map((id) => roles.find((r) => r.id === id))
        .filter(Boolean);
      groupRoles.forEach((r) => used.add(r.id));
      return groupRoles.length ? (
        <optgroup key={group} label={group}>
          {groupRoles.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
        </optgroup>
      ) : null;
    }).concat(
      roles.filter((r) => !used.has(r.id)).map((r) => <option key={r.id} value={r.id}>{r.label}</option>)
    );
  };

  if (initializing) {
    return (
      <div className="min-h-screen bg-[#050505]" data-testid="interview-page">
        <Navbar />
        <DashSidebar />
        <main className="lg:ml-60 pt-20 sm:pt-24 px-4 sm:px-6 md:px-10 pb-4 max-w-4xl mx-auto min-h-screen flex flex-col">
          <div className="mb-4 shrink-0">
            <div className="text-xs tracking-[0.2em] uppercase text-blue-500 font-semibold mb-2">// Mock Interview · Scored</div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl tracking-tight font-medium">Interview Simulator</h1>
            <p className="text-sm text-zinc-500 mt-2">Loading your interview...</p>
          </div>
          <div className="flex-1 flex items-center justify-center">
            <div className="w-full max-w-lg bg-[#0F0F12] border border-white/10 rounded-xl p-8 text-center">
              <div className="mx-auto mb-4 h-10 w-10 rounded-full border-2 border-white/10 border-t-blue-500 animate-spin" />
              <p className="text-sm text-zinc-500">Checking for your previous interview...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="interview-page">
      <Navbar />
      <DashSidebar />
      <main className="lg:ml-60 pt-20 sm:pt-24 px-4 sm:px-6 md:px-10 pb-4 max-w-4xl mx-auto min-h-screen flex flex-col">
        <div className="mb-4 shrink-0">
          <div className="text-xs tracking-[0.2em] uppercase text-blue-500 font-semibold mb-2">// Mock Interview · Scored</div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl tracking-tight font-medium">Interview Simulator</h1>
          <p className="text-sm text-zinc-500 mt-2">Practice for any role with AI-scored technical, behavioural and situational questions.</p>
          <p className="text-xs text-zinc-600 mt-2">{paid ? `${usage?.used?.interview_sessions || 0}/${usage?.limits?.interview_sessions || 0} sessions used this month` : "Free demo: 1 session · up to 3 answers"}</p>
        </div>

        {!session || setupMode ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="bg-[#0F0F12] border border-white/10 rounded-xl p-7 sm:p-8 max-w-lg w-full">
              <Microphone size={46} weight="duotone" className="mx-auto text-blue-500 mb-4" />
              <h2 className="text-2xl font-medium mb-2 text-center">{setupMode ? "Start a new interview" : "Choose your interview role"}</h2>
              <p className="text-sm text-zinc-500 mb-6 text-center">
                Select a role below, or enter any job title you want to practice.
              </p>

              <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-2">Interview role</label>
              <select
                value={roleId}
                onChange={(e) => { setRoleId(e.target.value); if (e.target.value !== "general") setCustomRole(""); }}
                data-testid="interview-role-select"
                className="w-full bg-[#050505] border border-white/10 rounded-md px-4 py-3 mb-3 outline-none focus:border-blue-500"
              >
                <option value="" disabled>Choose a role to practice</option>
                {renderRoleOptions()}
              </select>

              <div className="text-center text-xs text-zinc-600 my-3">OR</div>

              <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-2">Any other role</label>
              <input
                value={customRole}
                onChange={(e) => { setCustomRole(e.target.value); setRoleId(e.target.value ? "general" : roleId); }}
                data-testid="interview-custom-role"
                placeholder="e.g. BIM Coordinator, Cybersecurity Analyst, Sales Manager"
                className="w-full bg-[#050505] border border-white/10 rounded-md px-4 py-3 mb-4 outline-none focus:border-blue-500"
              />

              {(!paid && demoUsed) && (
                <div className="mb-4 rounded-xl border border-blue-500/25 bg-blue-500/5 p-4 text-center">
                  <div className="text-sm font-medium text-white">Free interview demo already used</div>
                  <p className="text-xs text-zinc-500 mt-1">Upgrade to Pro or Premium for full interview sessions.</p>
                  <button type="button" onClick={() => window.location.assign("/pricing")} className="mt-3 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg">Upgrade</button>
                </div>
              )}
              {paid && paidSessionLimitReached && (
                <div className="mb-4 rounded-xl border border-blue-500/25 bg-blue-500/5 p-4 text-center">
                  <div className="text-sm font-medium text-white">Monthly interview limit reached</div>
                  <p className="text-xs text-zinc-500 mt-1">You have used {paidSessionsUsed}/{paidSessionLimit} sessions this month.</p>
                  {user?.tier === "pro" && <button type="button" onClick={() => window.location.assign("/pricing")} className="mt-3 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg">Upgrade to Premium</button>}
                </div>
              )}

              <button
                onClick={start}
                disabled={busy || (!roleId && !customRole.trim()) || (!paid && demoUsed) || paidSessionLimitReached}
                data-testid="interview-start-btn"
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium px-6 py-3 rounded-lg inline-flex items-center justify-center gap-2"
              >
                <Play size={16} weight="fill" /> {busy ? "Starting..." : "Start Interview"}
              </button>

              {previousSession && (
                <button
                  type="button"
                  onClick={resumePrevious}
                  disabled={busy}
                  className="w-full mt-3 border border-white/10 hover:border-blue-500/40 text-zinc-300 hover:text-white px-6 py-3 rounded-lg inline-flex items-center justify-center gap-2 transition-colors"
                >
                  <ArrowLeft size={16} /> Continue previous {previousSession.session.role} interview
                </button>
              )}
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-3 text-xs mb-3 shrink-0">
              <div className="text-zinc-500">Role: <span className="text-blue-400">{session.role}</span></div>
              <div className="flex items-center gap-2">
                {avgScore !== null && (
                  <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-full px-3 py-1 inline-flex items-center gap-1.5 text-emerald-300" data-testid="interview-avg-score">
                    <Sparkle size={12} weight="fill" /> Avg score: <span className="font-mono font-medium">{avgScore}</span>/100
                  </div>
                )}
                <button
                  type="button"
                  onClick={startNew}
                  disabled={busy}
                  data-testid="interview-new-btn"
                  className="border border-white/10 hover:border-blue-500/50 bg-white/[0.03] hover:bg-blue-500/10 rounded-full px-3 py-1.5 inline-flex items-center gap-1.5 text-zinc-300 hover:text-white transition-colors"
                >
                  <Plus size={12} weight="bold" /> New interview
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto space-y-4 pr-2" data-testid="interview-messages">
              {messages.map((m) => (
                <div key={m.key}>
                  {m.role === "assistant" && m.feedback && (
                    <div className="ml-11 mb-2 bg-emerald-500/5 border border-emerald-500/20 rounded-lg px-3 py-2 text-xs text-emerald-200" data-testid="interview-feedback">
                      <span className="font-mono text-emerald-400">{m.score}/100</span> · {m.feedback}
                    </div>
                  )}
                  <div className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}>
                    {m.role === "assistant" && <div className="w-8 h-8 rounded-full bg-blue-600/20 border border-blue-500/40 flex items-center justify-center shrink-0"><Microphone size={14} className="text-blue-400" /></div>}
                    <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${m.role === "user" ? "bg-blue-600 text-white" : "bg-[#0F0F12] border border-white/10"}`}>
                      <div className="whitespace-pre-wrap text-sm leading-relaxed">{m.content}</div>
                    </div>
                    {m.role === "user" && <div className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shrink-0"><User size={14} /></div>}
                  </div>
                </div>
              ))}
              <div ref={endRef} />
            </div>

            <div className="mt-4 shrink-0">
              {demoLocked ? (
                <div className="rounded-xl border border-blue-500/25 bg-blue-500/5 p-5 text-center">
                  <div className="text-sm font-medium text-white">Free interview demo complete</div>
                  <p className="text-xs text-zinc-500 mt-1">You used all 3 free answers. Upgrade to Pro or Premium for full interview sessions.</p>
                  <button type="button" onClick={() => window.location.assign("/pricing")} className="mt-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-5 py-2.5 rounded-lg">Upgrade to continue</button>
                </div>
              ) : (
                <div className="bg-[#0F0F12] border border-white/10 focus-within:border-blue-500 rounded-xl flex items-center px-4 py-2 transition-colors">
                  <input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && send()}
                    placeholder="Your answer..."
                    data-testid="interview-input"
                    className="flex-1 bg-transparent text-white outline-none py-2"
                  />
                  <button onClick={send} disabled={busy || !input.trim()} data-testid="interview-send" className="bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white p-2 rounded-lg">
                    <PaperPlaneRight size={18} weight="fill" />
                  </button>
                </div>
              )}
            </div>

            <div className="mt-3 text-center">
              <button type="button" onClick={startNew} disabled={busy} className="text-xs text-zinc-600 hover:text-zinc-300 inline-flex items-center gap-1.5">
                <ClockCounterClockwise size={13} /> Start a different role / new interview
              </button>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
